import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ScanFace, Clock, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import BadgeVerified from "@/components/badges/badge_verified";
import ButtonMain from "@/components/buttons/button_main";
import { useIntegrations, providerReady, isNotConfigured } from "@/context/IntegrationsContext";

// Identity verification via DIDIT: a one time check for every signed in
// account (renters, owners and realtors alike) — SwipRent is a verified
// community, so swiping, matching, messaging and payments all unlock here.
// "Get verified" creates a real Didit session server side and sends the user
// to Didit's hosted flow (ID scan + selfie). The decision is usually instant;
// `verified` flips ONLY when Didit's signed webhook approves — nothing here
// ever fakes a verified state.

const STEPS = [
  { n: 1, label: "Get verified", icon: ShieldCheck, hint: "Start your request" },
  { n: 2, label: "Identity check", icon: ScanFace, hint: "Scan your ID and take a selfie" },
  { n: 3, label: "Decision", icon: Clock, hint: "Usually instant, sometimes a short review" },
  { n: 4, label: "You're verified", icon: BadgeCheck, hint: "Badge and full access" },
];

function stepFor(user, kycStatus) {
  // Verified means the whole journey is over: return 5 so step 4 renders as
  // Completed too (the Stepper marks a step done only when step > n).
  if (user?.verified || user?.lister_verified || kycStatus === "approved") return 5;
  if (kycStatus === "pending") return 3;
  return 1;
}

function Stepper({ step }) {
  return (
    <ol className="mt-5 space-y-3">
      {STEPS.map(({ n, label, icon: Icon, hint }) => {
        const done = step > n;
        const active = step === n;
        const state = done ? "Completed" : active ? "In progress" : "Pending";
        return (
          <li key={n} className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                done
                  ? "bg-brand-primary text-white"
                  : active
                    ? "bg-brand-ink text-white"
                    : "bg-neutral-100 text-brand-secondary"
              }`}
            >
              <Icon size={15} />
            </span>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${active || done ? "text-brand-ink" : "text-brand-secondary"}`}>
                {label}
              </p>
              <p className="text-xs text-brand-secondary">{hint}</p>
            </div>
            <span
              className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                done
                  ? "bg-brand-tint text-brand-lime"
                  : active
                    ? "bg-amber-50 text-amber-700"
                    : "bg-neutral-100 text-brand-secondary"
              }`}
            >
              {state}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default function VerificationCard({ user, onStatusChange }) {
  const { status: integrations } = useIntegrations();
  const [kycStatus, setKycStatus] = useState(user?.kyc_status || "unverified");
  const [verified, setVerified] = useState(!!user?.verified || !!user?.lister_verified);
  const [starting, setStarting] = useState(false);
  const [diditDown, setDiditDown] = useState(false);
  const providerReadyNow = providerReady(integrations, "didit") && !diditDown;

  // Refresh from the server on mount AND when the tab regains focus — the
  // user comes BACK from Didit's hosted flow, so this is when the webhook
  // driven status becomes visible without a manual reload.
  useEffect(() => {
    let alive = true;
    async function refresh() {
      try {
        const { data } = await api.get("/auth/me");
        const u = data?.user;
        if (!alive || !u) return;
        setVerified(!!u.verified || !!u.lister_verified);
        setKycStatus(u.kyc_status || "unverified");
      } catch {
        /* offline: keep optimistic state */
      }
    }
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  async function getVerified() {
    setStarting(true);
    try {
      const res = await api.post("/kyc/didit/session");
      const url = res.data?.url;
      if (url) {
        onStatusChange?.("pending");
        // Straight into Didit's hosted flow; they come back to /profile.
        window.location.href = url;
        return;
      }
      toast.warning("We could not start verification. Please try again shortly.");
    } catch (error) {
      if (isNotConfigured(error)) {
        setDiditDown(true);
        toast.info("Verification is not available yet. Please check back soon.");
      } else {
        const detail = error?.response?.data?.detail;
        toast.warning(typeof detail === "string" ? detail : "We could not start verification. Please try again shortly.");
      }
    } finally {
      setStarting(false);
    }
  }

  // Every signed in account can verify (renters included); demo sessions
  // never see the card because they cannot reach the real Didit flow.
  if (!user || user.demo) return null;

  const step = stepFor({ verified }, kycStatus);

  return (
    <section className="mt-4 rounded-3xl border border-brand-line bg-white p-6 shadow-hairline">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-tint text-brand-lime">
            <ShieldCheck size={18} />
          </span>
          <div>
            <h2 className="text-[15px] font-semibold text-brand-ink">Identity verification</h2>
            <p className="text-xs text-brand-secondary">
              A one time check with your ID and a selfie. Unlocks swiping, matching, messaging and payments.
            </p>
          </div>
        </div>
        {verified ? (
          <BadgeVerified label="Verified" className="border border-brand-line" />
        ) : (
          <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-brand-secondary">
            {kycStatus === "pending" ? "In review" : "Not verified"}
          </span>
        )}
      </div>

      <Stepper step={step} />

      {verified ? (
        <div className="mt-5 rounded-2xl bg-brand-tint px-4 py-3 text-sm text-brand-ink">
          You're verified. Your badge shows on every listing and you have full access.
        </div>
      ) : kycStatus === "pending" ? (
        <div className="mt-5">
          <div className="rounded-2xl bg-neutral-50 px-4 py-3 text-sm text-brand-secondary">
            Your verification is in progress. If you closed the check before finishing, start it
            again below. Your badge turns on automatically the moment it clears.
          </div>
          <ButtonMain className="mt-3" onClick={getVerified} disabled={!providerReadyNow || starting}>
            {starting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Starting
              </span>
            ) : (
              "Continue verification"
            )}
          </ButtonMain>
        </div>
      ) : !providerReadyNow ? (
        <div className="mt-5 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Verification is not available yet. It turns on once our identity provider is connected.
        </div>
      ) : (
        <div className="mt-5">
          <ButtonMain onClick={getVerified} disabled={starting}>
            {starting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Starting
              </span>
            ) : (
              "Get verified"
            )}
          </ButtonMain>
        </div>
      )}
    </section>
  );
}
